import { Divider } from '@mui/material';
import { OptionallyVisible } from 'components';
import { FieldArrayRenderProps, getIn, useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'components/form/formField/formField';
import IconAdd from 'components/icons/add';
import IconRemove from 'components/icons/dash';
import FileDropzone from 'components/inputs/file/dropzone';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { SelectItem } from 'components/inputs/select/select';

import { FormType, LABELS, imgAcceptRules } from '../../keys';
import { Section } from '../../styles';
import { BridgeDocumentPurpose, DocumentType } from '../../types';
import { createFileUploadHandler } from '../../useFileUpload';
import { createFormikChangeHandler, getCombinedError, getSectionErrors, hasFieldError } from '../../utils/formikHelpers';
import { isFieldDisabledInPartialMode } from '../../utils/partialUpdateHelpers';
import replaceLabelsUnderscore from '../../utils/replaceLabelsUnderscore';
import FormikInput from '../formikTextInput/formikTextInput';
import { Block, Container, InnerLabel, Label, RecordButton } from './styles';

interface DocumentsProps {
  // path to the field in the formik values, must point to a field of an array type: DocumentType[]
  fieldPath: string;
  insert: FieldArrayRenderProps['insert'];
  remove: FieldArrayRenderProps['remove'];

  //  for errors coming from backend
  error?: Record<string, string>;
  clearBackendErrors?: () => void;
  isPartialMode?: boolean;
}

const PURPOSES: SelectItem[] = Object.entries(BridgeDocumentPurpose).map(([key, value]) => ({
  key: value,
  label: key,
}));

const formattedPurposes = replaceLabelsUnderscore(PURPOSES);
const partiallyFormattedPurposes = formattedPurposes.filter((item) => item.key === BridgeDocumentPurpose.PROOF_OF_ADDRESS);

const Documents = ({ fieldPath, error, insert, remove, clearBackendErrors, isPartialMode = false }: DocumentsProps) => {
  const { t } = useTranslation();
  const { values, touched, errors, setFieldValue } = useFormikContext<FormType>();
  const documentsFormValue = getIn(values, fieldPath) as DocumentType[];
  const hasDocuments = Array.isArray(documentsFormValue) && documentsFormValue.length > 0;
  const defaultDocument: DocumentType[] = [{ purposes: [], file: undefined, description: undefined }];
  const documents = hasDocuments ? documentsFormValue : defaultDocument;
  const purposeOptions = isPartialMode ? partiallyFormattedPurposes : formattedPurposes;

  const disabled = isFieldDisabledInPartialMode(fieldPath, isPartialMode);

  const addItem = () => {
    insert(documents.length + 1, { purposes: [], file: undefined, description: undefined });
  };

  const removeItem = (index: number) => {
    return () => {
      remove(index);
    };
  };

  return (
    <>
      <Label>{t(LABELS.SECTIONS.DOCUMENTS.TITLE)}</Label>

      {getSectionErrors(error, fieldPath).map((message) => (
        <FormField key={message} error={message}>
          {null}
        </FormField>
      ))}

      <Block>
        <Section>
          {documents.map((_, index) => {
            const purposes = `${fieldPath}[${index}].purposes`;
            const file = `${fieldPath}[${index}].file`;
            const description = `${fieldPath}[${index}].description`;

            const purposeError = getIn(errors, purposes);
            const purposeTouched = getIn(touched, purposes);

            const fileVal = getIn(values, file);
            const selectedPurposes = getIn(values, purposes) || [];
            const handleFileChange = createFileUploadHandler(setFieldValue, file, fileVal);

            const fileError = getIn(errors, file);
            const fileTouched = getIn(touched, file);

            return (
              <Container>
                <FormField error={getCombinedError(error, purposes, purposeError, purposeTouched)}>
                  <SearchableSelect<SelectItem, true>
                    fullWidth
                    value={purposeOptions.filter((item) => selectedPurposes.includes(item.key))}
                    items={purposeOptions}
                    placeholder={t(LABELS.SECTIONS.DOCUMENTS.PLACEHOLDER_PURPOSE)}
                    onChange={createFormikChangeHandler(
                      (selectItems) => setFieldValue(purposes, selectItems?.map((item) => item.key)),
                      clearBackendErrors,
                    )}
                    getItemLabel={(item) => item.label}
                    multiple
                    closeDropdownOnSelect
                    error={hasFieldError(error, purposes, purposeError, purposeTouched)}
                    disabled={disabled}
                  />
                </FormField>

                <FormField error={getCombinedError(error, file, fileError, fileTouched)}>
                  <InnerLabel>{t(LABELS.SECTIONS.DOCUMENTS.LOGO.UPLOAD)}</InnerLabel>
                  <FileDropzone
                    error={fileError && fileTouched ? fileError : undefined}
                    onFileChange={createFormikChangeHandler(handleFileChange, clearBackendErrors)}
                    filledInputLabel={t(LABELS.SECTIONS.DOCUMENTS.LOGO.LABEL)}
                    initialFile={fileVal}
                    hints={[t(LABELS.SECTIONS.DOCUMENTS.LOGO.SUPPORTED_FORMAT), t(LABELS.SECTIONS.DOCUMENTS.LOGO.MAX_FILE_SIZE)]}
                    accept={imgAcceptRules}
                    disabled={disabled}
                    maxFileSizeInMB={20}
                  />
                </FormField>

                <FormikInput
                  placeholder={t(LABELS.SECTIONS.DOCUMENTS.PLACEHOLDER_DESCRIPTION)}
                  fieldPath={description}
                  error={error}
                  disabled={disabled}
                />

                <OptionallyVisible visible={index > 0}>
                  <div>
                    <RecordButton type="button" inside flat onClick={removeItem(index)} disabled={disabled}>
                      <IconRemove />
                      {t(LABELS.SECTIONS.DOCUMENTS.REMOVE)}
                    </RecordButton>
                  </div>
                </OptionallyVisible>

                <OptionallyVisible visible={index !== documents.length - 1}>
                  <Divider />
                </OptionallyVisible>
              </Container>
            );
          })}
        </Section>
      </Block>

      <RecordButton type="button" flat onClick={addItem} disabled={disabled}>
        <IconAdd />
        {t(LABELS.SECTIONS.DOCUMENTS.ADD)}
      </RecordButton>
    </>
  );
};

export default Documents;
